/**
 * What the browser is allowed to hand the uploader, and what it turns a photo into first.
 *
 * The rules here mirror `apps/server/src/services/storage/upload-policy.ts`. The server is still
 * the authority — it re-reads the size and the content type off the bucket before a row exists —
 * and this exists only so a refusal costs a sentence instead of a five-megabyte round trip. The
 * numbers are duplicated rather than imported: that module reads `node:crypto`, which has no
 * business in a browser bundle.
 *
 * Downscaling is the other half. A phone camera hands over a 12 MP JPEG for something that is
 * drawn as a circle a couple of hundred pixels across, so the bytes are re-encoded before they
 * are uploaded and it is the *result* the player waits on.
 */

/** The types the server signs a PUT for. An allow-list, and the `accept` attribute's source. */
export const UPLOAD_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

export type UploadContentType = (typeof UPLOAD_CONTENT_TYPES)[number];

/** 5 MB — `MAX_UPLOAD_BYTES` on the server, applied here to what we are about to send. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/**
 * The longest edge anything is stored at. Nothing renders an avatar or a circle bigger than
 * this, so pixels past it are bytes the player uploads and nobody ever sees.
 */
export const MAX_IMAGE_DIMENSION = 512;

/** What a re-encode produces. WebP keeps transparency, which a circle cut from a PNG needs. */
const REENCODE_CONTENT_TYPE: UploadContentType = 'image/webp';
const REENCODE_QUALITY = 0.9;

/** For `<input type="file" accept>`. */
export const UPLOAD_ACCEPT_ATTRIBUTE = UPLOAD_CONTENT_TYPES.join(',');

/** A file the player chose that we will not send. The message is shown to them verbatim. */
export class ImageRejectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageRejectedError';
  }
}

/** The bytes to PUT, the type they were signed for, and the dimensions `confirmUpload` records. */
export interface PreparedImage {
  blob: Blob;
  contentType: UploadContentType;
  width: number;
  height: number;
}

export function isUploadContentType(value: string): value is UploadContentType {
  return (UPLOAD_CONTENT_TYPES as readonly string[]).includes(value);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** `image/jpeg; charset=binary` -> `image/jpeg`, the same normalisation the server does. */
function normalizeContentType(value: string): string {
  return value.split(';')[0]?.trim().toLowerCase() ?? '';
}

/**
 * Validate a chosen file and produce the bytes to upload.
 *
 * Throws `ImageRejectedError` — and never touches the network — for anything the server would
 * refuse anyway. A file that passes is decoded and, if it is larger than `MAX_IMAGE_DIMENSION`,
 * redrawn at that size; if the browser will not give us a canvas or a blob back, the original
 * file is uploaded unchanged rather than the upload failing.
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  const contentType = normalizeContentType(file.type);

  if (!isUploadContentType(contentType)) {
    throw new ImageRejectedError('Images must be a PNG, JPEG or WebP file.');
  }
  if (file.size === 0) {
    throw new ImageRejectedError('That file is empty.');
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new ImageRejectedError(
      `Images must be under ${formatBytes(MAX_UPLOAD_BYTES)}. That one is ${formatBytes(file.size)}.`,
    );
  }

  const source = await decode(file);
  try {
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(source.width, source.height));
    if (scale === 1) {
      return { blob: file, contentType, width: source.width, height: source.height };
    }

    const width = Math.max(1, Math.round(source.width * scale));
    const height = Math.max(1, Math.round(source.height * scale));
    const resized = await redraw(source, width, height);

    // A browser that cannot re-encode is not a reason to refuse the upload: the original is
    // already under the size cap, it is only bigger than it needs to be.
    if (!resized) {
      return { blob: file, contentType, width: source.width, height: source.height };
    }

    return { blob: resized.blob, contentType: resized.contentType, width, height };
  } finally {
    source.close();
  }
}

async function decode(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    // Right extension, wrong bytes — a renamed file, or one truncated by the file picker.
    throw new ImageRejectedError('That file could not be read as an image.');
  }
}

/** The canvas half, separated so `prepareImage` reads as policy rather than as drawing. */
async function redraw(
  source: ImageBitmap,
  width: number,
  height: number,
): Promise<{ blob: Blob; contentType: UploadContentType } | null> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) return null;
  context.drawImage(source, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, REENCODE_CONTENT_TYPE, REENCODE_QUALITY);
  });
  if (!blob) return null;

  // `toBlob` falls back to PNG when it cannot encode the type it was asked for, so the type is
  // read off what came back — it becomes part of the presigned signature and has to be exact.
  const encoded = normalizeContentType(blob.type);
  if (!isUploadContentType(encoded)) return null;

  return { blob, contentType: encoded };
}
